import React from "react";
import type { ApolloClient } from "@apollo/client";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import { push } from "store/actions/router";
import { Col, Row } from "react-bootstrap";
import moment from "moment";

import Alert from "components/Alert";
import PromisePoller, {
  type PollingRenderProps,
} from "components/PromisePoller";
import AddBankCard from "pages/dashboards/AddBankCard";
import SchedulePicker from "./SchedulePicker";
import SelfEmployedContributionSelection from "./SelfEmployedContributionSelection";
import type { Schedule } from "./scheduleTypes";

import { getAllAccounts } from "actions/accountActions";
import { getUserProfile } from "actions/userActions";
import { getContributionAbility } from "actions/contributionActions";
import { createLoadingSelector } from "store/selectors";
import { accountConstants } from "actions/types";
import {
  isBankAwaitingAchLinkSelector,
  isBankVerifiedSelector,
} from "store/selectors/bank";
import { userIsPaychexPerks } from "store/selectors/user";
import "./SetupContribution.scss";
import AddEmployerCard from "pages/signUp/individual/setupContribution/AddEmployerCard";
import type { RootState } from "types/state";

const loadingSelector = createLoadingSelector(accountConstants.GET_ACCOUNTS);

const BANK_POLL_INTERVAL_MS = 3000;
const BANK_POLL_MINUTES = 1;

const mapStateToProps = (state: RootState) => {
  return {
    hasVerifiedBankAccount: isBankVerifiedSelector(state),
    isAwaitingAchLink: isBankAwaitingAchLinkSelector(state),
    isFetching: loadingSelector(state),
    isPaychexPerks: userIsPaychexPerks(state),
  };
};
const mapDispatchToProps = {
  push,
  getAllAccounts,
  getUserProfile,
  getContributionAbility,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  hasConfiguredSchedule: boolean;
  schedule: Schedule;
  modifier: string;
  contributionAmount: string;
}

class SelfEmployedFlow extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      hasConfiguredSchedule: false,
      schedule: {
        frequency: "",
        dayOne: "",
        anchorPayDate: "",
      },
      modifier: "",
      contributionAmount: "",
    };
  }

  componentDidMount() {
    void this.props.getAllAccounts(this.props.client as ApolloClient<object>);
    void this.props.getUserProfile(this.props.client as ApolloClient<object>);
    void this.props.getContributionAbility(
      this.props.client as ApolloClient<object>
    );
  }

  _pollForLinkedBank = () => {
    return this.props.getAllAccounts(this.props.client as ApolloClient<object>);
  };

  _handleBankLinked = () => {
    void this.props.getAllAccounts(this.props.client as ApolloClient<object>);
  };

  _renderPollProgress = ({ isPolling }: PollingRenderProps) => {
    if (isPolling) {
      return null;
    }
    return (
      <Alert
        type="warning"
        msg="Your bank account is taking longer than usual to finish connecting. Reload this page in a few minutes to set up your contribution — you don't need to link it again."
      />
    );
  };

  _setSchedule = (schedule: Schedule) => {
    this.setState({ schedule, hasConfiguredSchedule: true });
  };

  _editSchedule = () => {
    this.setState({
      hasConfiguredSchedule: false,
      schedule: {
        ...this.state.schedule,
        anchorPayDate: moment(this.state.schedule.anchorPayDate),
      },
    });
  };

  _getPageTitle() {
    if (this.state.hasConfiguredSchedule) {
      return (
        <section className="page-title-wrap">
          <article className="text-cell">
            <h1 className="page-title">
              How much would you like to contribute?
            </h1>
            <p className="page-subtext">
              Choose what works for you or enter a custom amount—you can always
              change it later.
            </p>
          </article>
        </section>
      );
    } else if (this.props.hasVerifiedBankAccount) {
      return (
        <section className="page-title-wrap">
          <article className="text-cell">
            <h1 className="page-title">
              You’re bank account is now connected — let’s set up your
              contributions.
            </h1>
            <p className="page-subtext">
              Whether it&apos;s weekly, or monthly, consistent contributions add
              up over time. Don&apos;t worry—you can change this anytime.
            </p>
          </article>
        </section>
      );
    } else {
      return (
        <section className="page-title-wrap">
          <article className="text-cell">
            <h1 className="page-title">
              Let{"'"}s get started with setting up your contributions!
            </h1>
            <p className="page-subtext">
              {this.props.isPaychexPerks
                ? "First you will link your bank account and then setup a recurring contribution into your retirement account."
                : "First you will link your bank account or employer account and then setup a recurring contribution into your retirement account."}
            </p>
          </article>
        </section>
      );
    }
  }

  render() {
    let content;

    if (!this.props.hasVerifiedBankAccount) {
      content = (
        <>
          <Row className="justify-content-md-center">
            <Col md={6}>
              <AddBankCard onSuccess={this._handleBankLinked} />
            </Col>
            {!this.props.isPaychexPerks && (
              <Col md={6}>
                <AddEmployerCard />
              </Col>
            )}
          </Row>
          {this.props.isAwaitingAchLink && (
            <PromisePoller
              interval={BANK_POLL_INTERVAL_MS}
              minutesToPollFor={BANK_POLL_MINUTES}
              promiseToPoll={this._pollForLinkedBank}
              renderFunction={this._renderPollProgress}
            />
          )}
        </>
      );
    } else if (!this.state.hasConfiguredSchedule) {
      content = (
        <SchedulePicker
          schedule={this.state.schedule}
          setSchedule={this._setSchedule}
        />
      );
    } else {
      content = (
        <SelfEmployedContributionSelection
          editSchedule={this._editSchedule}
          schedule={this.state.schedule}
        />
      );
    }
    return (
      <Row className="setup-contributions-row">
        <Col md={12} style={{ textAlign: "center" }}>
          <div className="contribution-setup-wrapper">
            {this._getPageTitle()}
            {content}
          </div>
        </Col>
      </Row>
    );
  }
}

export default connector(withApollo<Props>(SelfEmployedFlow));
